import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { CheckSquare, Circle, Clock, ArrowSquareOut, AirplaneTilt, Anchor, WarningOctagon, Lightning, ClipboardText } from '@phosphor-icons/react';

const API = '/api';

export default function Tasks() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterView, setFilterView] = useState('all');

  useEffect(() => { loadTasks(); }, []);

  const loadTasks = async () => {
    try {
      const { data } = await axios.get(`${API}/tasks`, { withCredentials: true });
      setTasks(data);
    } catch (error) {
      console.error('Failed to load tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTasks = tasks.filter(task => {
    if (filterView === 'all') return true;
    if (filterView === 'my-tasks') return task.assigned_to === user?.email;
    if (filterView === 'pending') return task.status === 'pending';
    if (filterView === 'in_progress') return task.status === 'in_progress';
    if (filterView === 'escalated') return task.escalated && task.status !== 'completed';
    if (filterView === 'completed') return task.status === 'completed';
    return true;
  });

  const getStatusIcon = (status, escalated) => {
    if (escalated && status !== 'completed') return <Lightning size={18} weight="fill" className="text-red-600" />;
    if (status === 'completed') return <CheckSquare size={18} weight="fill" className="text-green-600" />;
    if (status === 'in_progress') return <Circle size={18} weight="duotone" className="text-blue-600" />;
    return <Clock size={18} weight="duotone" className="text-amber-600" />;
  };

  const getPriorityColor = (p) => {
    const map = { critical: 'bg-red-600 text-white', high: 'bg-red-100 text-red-800', medium: 'bg-amber-100 text-amber-800', low: 'bg-green-100 text-green-800' };
    return map[p] || 'bg-slate-100 text-slate-800';
  };

  const getStatusColor = (s) => {
    const map = { pending: 'bg-slate-200 text-slate-700', in_progress: 'bg-blue-100 text-blue-800', completed: 'bg-green-100 text-green-800', escalated: 'bg-red-100 text-red-800' };
    return map[s] || 'bg-slate-100 text-slate-800';
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '-';

  const escalatedCount = tasks.filter(t => t.escalated && t.status !== 'completed').length;

  return (
    <div className="" data-testid="tasks-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>ICAO 9756 Tasks</h1>
          <p className="text-slate-500">Investigation tasks generated from ICAO 9756 Annex 13 standard templates</p>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
          <div className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>{tasks.length}</div>
          <div className="text-xs text-slate-500">Total Tasks</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
          <div className="text-2xl font-bold text-amber-600" style={{ fontFamily: 'Outfit' }}>{tasks.filter(t => t.status === 'pending').length}</div>
          <div className="text-xs text-slate-500">Pending</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
          <div className="text-2xl font-bold text-blue-600" style={{ fontFamily: 'Outfit' }}>{tasks.filter(t => t.status === 'in_progress').length}</div>
          <div className="text-xs text-slate-500">In Progress</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
          <div className="text-2xl font-bold text-green-600" style={{ fontFamily: 'Outfit' }}>{tasks.filter(t => t.status === 'completed').length}</div>
          <div className="text-xs text-slate-500">Completed</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
          <div className={`text-2xl font-bold ${escalatedCount > 0 ? 'text-red-600' : 'text-slate-400'}`} style={{ fontFamily: 'Outfit' }}>{escalatedCount}</div>
          <div className="text-xs text-slate-500">Escalated</div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="bg-white rounded-xl p-2 border border-slate-200 mb-6 flex gap-2 flex-wrap" data-testid="task-filters">
        {[
          { key: 'all', label: 'All Tasks' },
          { key: 'my-tasks', label: 'My Tasks' },
          { key: 'pending', label: 'Pending' },
          { key: 'in_progress', label: 'In Progress' },
          { key: 'escalated', label: 'Escalated' },
          { key: 'completed', label: 'Completed' },
        ].map(f => (
          <Button key={f.key} variant={filterView === f.key ? 'default' : 'ghost'} onClick={() => setFilterView(f.key)}
            className={filterView === f.key ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white' : ''} data-testid={`filter-${f.key}`}>
            {f.label}
          </Button>
        ))}
      </div>

      {/* Tasks List */}
      <div className="space-y-3">
        {loading ? (
          <div className="bg-white rounded-xl p-12 text-center text-slate-500 border border-slate-200">Loading tasks...</div>
        ) : filteredTasks.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center border border-slate-200">
            <CheckSquare size={48} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No tasks found</h3>
            <p className="text-sm text-slate-500">Tasks are generated from ICAO 9756 templates when an investigation is assigned.</p>
            {(user?.role === 'admin' || user?.role === 'iic') && (
              <Button className="mt-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg" onClick={() => navigate('/investigations')} data-testid="go-investigations">
                Go to Investigations
              </Button>
            )}
          </div>
        ) : (
          filteredTasks.map((task) => (
            <div key={task.id} className={`bg-white rounded-xl p-5 border hover:shadow-md transition-shadow ${task.escalated && task.status !== 'completed' ? 'border-red-300 ring-1 ring-red-100' : 'border-slate-200'}`} data-testid={`task-row-${task.task_code}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                  {getStatusIcon(task.status, task.escalated)}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded w-[52px] text-center inline-block shrink-0">{task.task_code}</span>
                      <h3 className="font-semibold text-slate-900">{task.title}</h3>
                      {task.escalated && task.status !== 'completed' && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-600 text-white flex items-center gap-0.5">
                          <Lightning size={10} /> ESCALATED
                        </span>
                      )}
                    </div>
                    {task.description && <p className="text-sm text-slate-600 mt-1 line-clamp-1">{task.description}</p>}
                    <div className="flex flex-wrap gap-4 mt-2 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        {task.sector === 'aviation' ? <AirplaneTilt size={12} /> : <Anchor size={12} />}
                        {task.investigation_number}
                      </span>
                      <span>Start: <span className="font-medium text-slate-700">{formatDate(task.start_date)}</span></span>
                      <span>End: <span className="font-medium text-slate-700">{formatDate(task.due_date)}</span></span>
                      <span>Standard: {task.standard_days}d</span>
                      {task.assigned_to_name && <span>Assigned: <span className="font-medium text-slate-700">{task.assigned_to_name}</span></span>}
                      {task.days_remaining !== undefined && (
                        <span className={task.days_remaining < 0 ? 'text-red-600 font-bold' : task.days_remaining <= 3 ? 'text-amber-600 font-medium' : 'text-green-600'}>
                          {task.days_remaining < 0 ? `${Math.abs(task.days_remaining)}d overdue` : `${task.days_remaining}d remaining`}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-3 shrink-0">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(task.priority)}`}>{task.priority}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>{task.status?.replace('_', ' ')}</span>
                  <Button size="sm" variant="outline" className="rounded-lg h-8 px-2 border-blue-300 text-blue-700 hover:bg-blue-50" onClick={() => navigate(`/tasks/${task.id}`)} data-testid={`open-module-${task.task_code}`} title="Open Specialist Module">
                    <ClipboardText size={14} />
                  </Button>
                  {task.investigation_id && (
                    <Button size="sm" variant="outline" className="rounded-lg h-8 w-8 p-0" onClick={() => navigate(`/investigations/${task.investigation_id}`)} data-testid={`go-inv-${task.task_code}`} title="Open Investigation">
                      <ArrowSquareOut size={14} />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
